import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Trophy,
  TrendingUp,
  Target,
  Zap,
  Star,
  Award,
  Sparkles,
  BarChart3,
  Users,
  Globe,
  Rocket,
} from "lucide-react";

const achievements = [
  {
    id: 1,
    title: "SEO Performance",
    description: "Improved organic search rankings by 40% for client websites",
    icon: <TrendingUp />,
    color: "from-green-500 to-emerald-500",
    metrics: "+40% rankings",
    year: "2024",
  },
  {
    id: 2,
    title: "Website Performance",
    description: "Reduced website load time by 60% across all projects",
    icon: <Zap />,
    color: "from-yellow-500 to-amber-500",
    metrics: "-60% load time",
    year: "2023",
  },
  {
    id: 3,
    title: "Client Satisfaction",
    description: "Achieved 100% client satisfaction rate on all projects",
    icon: <Star />,
    color: "from-blue-500 to-cyan-500",
    metrics: "100% satisfaction",
    year: "2024",
  },
  {
    id: 4,
    title: "Traffic Growth",
    description: "Increased website traffic by 60% through targeted strategies",
    icon: <BarChart3 />,
    color: "from-purple-500 to-pink-500",
    metrics: "+60% traffic",
    year: "2023",
  },
  {
    id: 5,
    title: "Project Delivery",
    description: "Successfully delivered 20+ projects across various domains",
    icon: <Target />,
    color: "from-red-500 to-orange-500",
    metrics: "20+ projects",
    year: "2024",
  },
  {
    id: 6,
    title: "Certification Excellence",
    description: "Earned CCNP certification with distinction",
    icon: <Award />,
    color: "from-indigo-500 to-violet-500",
    metrics: "CCNP Certified",
    year: "2020",
  },
  {
    id: 7,
    title: "Team Leadership",
    description: "Led multiple successful projects at University level",
    icon: <Users />,
    color: "from-cyan-500 to-blue-500",
    metrics: "Team Lead",
    year: "2021",
  },
  {
    id: 8,
    title: "Global Recognition",
    description: "Projects featured and recognized in international platforms",
    icon: <Globe />,
    color: "from-pink-500 to-rose-500",
    metrics: "Global Reach",
    year: "2023",
  },
];

const ParticleCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let particles = [];
    let animationId;

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.color = `hsl(${Math.random() * 60 + 180}, 100%, 70%)`;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const init = () => {
      particles = [];
      for (let i = 0; i < 50; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      animationId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    init();
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />
  );
};

const AchievementCard = ({ achievement, index }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, rotateY: 180 }}
      whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative group"
    >
      {/* Card */}
      <div className="relative p-6 rounded-2xl glass-effect border border-white/10 h-full overflow-hidden">
        {/* Background Glow */}
        <div
          className={`absolute inset-0 bg-gradient-to-br ${achievement.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
        ></div>

        {/* Icon */}
        <div className="relative z-10">
          <div
            className={`p-3 rounded-xl bg-gradient-to-br ${achievement.color} w-14 h-14 flex items-center justify-center mb-4`}
          >
            {achievement.icon}
          </div>

          {/* Year Badge */}
          <div className="absolute top-0 right-0 px-3 py-1 rounded-full bg-white/10 text-xs">
            {achievement.year}
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold mb-2">{achievement.title}</h3>

          {/* Description */}
          <p className="text-gray-400 mb-4">{achievement.description}</p>

          {/* Metrics */}
          <div className="flex items-center justify-between">
            <div className="text-lg font-bold gradient-text">
              {achievement.metrics}
            </div>
            <motion.div
              animate={{ rotate: isHovered ? 360 : 0 }}
              transition={{ duration: 0.5 }}
            >
              <Sparkles className="text-yellow-400" />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Floating Particles */}
      {isHovered && (
        <>
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: Math.random() * 100 - 50,
                y: Math.random() * 100 - 50,
              }}
              transition={{
                duration: 1,
                delay: i * 0.2,
                repeat: Infinity,
              }}
              className="absolute w-2 h-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-400"
              style={{
                left: "50%",
                top: "50%",
              }}
            />
          ))}
        </>
      )}
    </motion.div>
  );
};

const Achievements = () => {
  const [activeFilter, setActiveFilter] = useState("all");

  const filteredAchievements =
    activeFilter === "all"
      ? achievements
      : achievements.filter((a) =>
          activeFilter === "performance"
            ? a.title.includes("Performance") || a.title.includes("Growth")
            : activeFilter === "client"
            ? a.title.includes("Client") || a.title.includes("Team")
            : a.title.includes("Certification") ||
              a.title.includes("Recognition")
        );

  return (
    <section id="achievements" className="py-20 relative overflow-hidden">
      {/* Background Canvas */}
      <div className="absolute inset-0">
        <ParticleCanvas />
      </div>

      {/* Gradient Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-primary-500/10 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-l from-secondary-500/10 to-transparent rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-effect border border-white/10 mb-4">
            <Trophy className="w-4 h-4 text-primary-400" />
            <span className="text-sm">Achievements</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Notable
            <span className="block gradient-text">Achievements</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Milestones and accomplishments that showcase my impact and expertise
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 flex justify-center"
        >
          <div className="inline-flex rounded-full glass-effect p-1">
            {[
              { key: "all", label: "All Achievements" },
              { key: "performance", label: "Performance" },
              { key: "client", label: "Client Success" },
              { key: "recognition", label: "Recognition" },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-4 py-2 rounded-full transition-all ${
                  activeFilter === filter.key
                    ? "bg-gradient-to-r from-primary-500 to-secondary-500"
                    : "hover:bg-white/10"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Achievements Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {filteredAchievements.map((achievement, index) => (
            <AchievementCard
              key={achievement.id}
              achievement={achievement}
              index={index}
            />
          ))}
        </div>

        {/* Stats Banner */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 rounded-3xl glass-effect border border-white/10 relative overflow-hidden"
        >
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-transparent to-secondary-500/10 animate-gradient"></div>

          <div className="relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                {
                  value: "9+",
                  label: "Years Experience",
                  icon: <TrendingUp />,
                },
                { value: "20+", label: "Projects Completed", icon: <Target /> },
                { value: "100%", label: "Client Satisfaction", icon: <Star /> },
                { value: "CCNP", label: "Top Certification", icon: <Award /> },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="inline-block p-3 rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 mb-3">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold gradient-text">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-300 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center space-x-2 px-6 py-3 rounded-full glass-effect border border-white/10">
            <Rocket className="w-5 h-5 text-primary-400 animate-pulse" />
            <span className="text-gray-300">
              Ready to achieve great results together?
            </span>
            <button className="ml-4 px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-primary-500/30 transition-all">
              Let's Connect
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Achievements;
